import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  Download,
  Archive,
  BookOpen,
  ShoppingCart,
  ArrowUpRight,
  Calendar,
  Lock,
  ExternalLink,
} from "lucide-react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";
import { getIssueBySlug, NEWSLETTER_META } from "@/data/newsletterIssues";
import {
  PermanentDisclaimer,
  AffiliateDisclosure,
} from "@/components/NewsletterDisclaimer";
import { useAuth } from "@/context/AuthContext";
import { NewsletterSignupModal } from "@/components/NewsletterSignupModal";

const SITE_URL = "https://ogonotary.com";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Public preview + full issue page.
 *
 * Non-members see: cover, table of contents, one excerpt per section, signup CTA.
 * Members / newsletter subscribers see: the complete web issue rendered from the
 * static HTML file supplied by Greg, wrapped in the OGO shell (sticky nav, buttons,
 * affiliate disclosure, permanent disclaimer).
 *
 * The gated iframe strategy preserves Greg's exact supplied newsletter content
 * verbatim while giving us full control over the surrounding chrome.
 */
export default function NewsletterIssuePage() {
  const { slug } = useParams();
  const issue = getIssueBySlug(slug);
  const { isAuthed } = useAuth();
  const [signupOpen, setSignupOpen] = React.useState(false);
  const [unlocked, setUnlocked] = React.useState(false);

  React.useEffect(() => {
    // Subscribers who just signed up in the modal land here with ?subscribed=1
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("subscribed") === "1") setUnlocked(true);
      // Persist a lightweight subscriber flag so returning subscribers keep
      // access without re-signing up. Members are handled via auth context.
      try {
        if (localStorage.getItem("ogo_newsletter_subscribed") === "1") {
          setUnlocked(true);
        }
      } catch (_e) {
        // localStorage unavailable — fine, fall through.
      }
    }
  }, []);

  if (!issue) return <Navigate to="/newsletter" replace />;

  const canonical = `${SITE_URL}/newsletter/${issue.slug}`;
  const canReadFull = isAuthed || unlocked;

  return (
    <>
      <Helmet>
        <title>{`${issue.title} | OGO Notary`}</title>
        <meta
          name="description"
          content={`Read Issue ${issue.number} of the OGO Weekly Notary Brief — ${issue.publication}. State Spotlight: ${issue.stateSpotlight}. Service Focus: ${issue.serviceFocus}.`}
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={issue.title} />
        <meta property="og:image" content={`${SITE_URL}${issue.cover}`} />
        <meta property="og:url" content={canonical} />
        <meta property="article:published_time" content={issue.publishedAt} />
      </Helmet>

      <PageHero
        eyebrow={`Issue ${issue.number} · ${issue.publication}`}
        title={issue.title}
        lead={`State Spotlight: ${issue.stateSpotlight} · Service Focus: ${issue.serviceFocus} · Client Growth: ${issue.clientGrowth} · Business Desk: ${issue.businessDesk}`}
      />

      {/* Top action bar */}
      <section className="border-b border-white/10 bg-[#0A1020]" data-testid="issue-top-bar">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-5 flex flex-wrap items-center gap-3 justify-between">
          <Link
            to="/newsletter"
            data-testid="issue-back-archive"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-300 hover:text-[#D4AF37]"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All Issues
          </Link>
          <IssueButtonRow issue={issue} />
        </div>
      </section>

      {canReadFull ? (
        <FullIssue issue={issue} />
      ) : (
        <PublicPreview
          issue={issue}
          onOpenSignup={() => setSignupOpen(true)}
        />
      )}

      {/* Bottom action bar */}
      <section className="border-t border-b border-white/10 bg-[#0A1020]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center gap-3 justify-center">
          <IssueButtonRow issue={issue} showAffiliateDisclosure />
        </div>
      </section>

      <PermanentDisclaimer />

      <NewsletterSignupModal
        open={signupOpen}
        onClose={(subscribed) => {
          setSignupOpen(false);
          if (subscribed) {
            try {
              localStorage.setItem("ogo_newsletter_subscribed", "1");
            } catch (_e) {}
            setUnlocked(true);
          }
        }}
      />
    </>
  );
}

function IssueButtonRow({ issue, showAffiliateDisclosure = false }) {
  return (
    <div className="flex flex-col items-center gap-3 w-full sm:w-auto">
      <div className="flex flex-wrap gap-2 justify-center">
        <a
          href={issue.pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="issue-download-pdf"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Download PDF
        </a>
        <Link
          to="/newsletter"
          data-testid="issue-view-archive"
          className="inline-flex items-center gap-2 border border-white/20 text-slate-200 hover:border-[#D4AF37] hover:text-[#D4AF37] px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
        >
          <Archive className="w-3.5 h-3.5" />
          Newsletter Archive
        </Link>
        <Link
          to="/resources"
          data-testid="issue-explore-resources"
          className="inline-flex items-center gap-2 border border-white/20 text-slate-200 hover:border-[#D4AF37] hover:text-[#D4AF37] px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5" />
          Explore Free Resources
        </Link>
        <a
          href={NEWSLETTER_META.amazonEssentialsUrl}
          target="_blank"
          rel="sponsored nofollow noopener noreferrer"
          data-testid="issue-shop-essentials"
          className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#FDE047] px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          Shop Notary Essentials
          <ArrowUpRight className="w-3 h-3" />
        </a>
      </div>
      {showAffiliateDisclosure && (
        <AffiliateDisclosure className="text-center max-w-2xl" />
      )}
    </div>
  );
}

function PublicPreview({ issue, onOpenSignup }) {
  return (
    <section className="py-16 sm:py-20" data-testid="issue-public-preview">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="mb-10 border border-[#D4AF37]/30 bg-[#0F1B2E]">
          <div className="aspect-[16/9] overflow-hidden bg-[#0B1120] border-b border-white/10">
            <img
              src={issue.cover}
              alt={issue.coverAlt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="p-6 sm:p-8">
            <SectionEyebrow className="mb-3">In This Issue</SectionEyebrow>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-slate-300 text-sm list-decimal list-inside">
              {issue.sections.map((s, i) => (
                <li key={i}>{s.headline}</li>
              ))}
            </ol>
          </div>
        </div>

        <div className="space-y-8">
          {issue.sections.map((s, i) => (
            <div
              key={i}
              data-testid={`preview-section-${i}`}
              className="border-l-2 border-[#D4AF37] pl-5 sm:pl-7"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">
                {s.kicker}
              </div>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-white leading-tight mb-3">
                {s.headline}
              </h3>
              <p className="text-slate-300 leading-relaxed">{s.excerpt}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 p-8 sm:p-10 border border-[#D4AF37] bg-[#D4AF37]/[0.06] text-center">
          <Lock className="w-6 h-6 text-[#D4AF37] mx-auto mb-4" />
          <h3 className="font-serif-display text-2xl sm:text-3xl text-white leading-tight mb-3">
            Read the Complete Issue — Free
          </h3>
          <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto mb-6">
            Enter your name and email to unlock the full web edition, download
            the printable PDF, and get future weekly issues delivered.
          </p>
          <button
            type="button"
            onClick={onOpenSignup}
            data-testid="preview-signup-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Send Me the Free Issue
          </button>
          <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-400">
            Already an OGO member? <Link to="/community" className="text-[#D4AF37] hover:text-[#FDE047]">Log in</Link>
          </p>
        </div>
      </div>
    </section>
  );
}

function FullIssue({ issue }) {
  return (
    <section className="py-8 sm:py-10" data-testid="issue-full-content">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="mb-4 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-slate-400">
          <span className="inline-flex items-center gap-1.5 text-[#D4AF37]">
            <Calendar className="w-3 h-3" />
            Verified through {issue.verifiedThrough}
          </span>
          <span>·</span>
          <span>Last reviewed {formatDate(issue.publishedAt)}</span>
        </div>
        <div
          className="border border-white/10 bg-white overflow-hidden"
          data-testid="issue-html-frame-wrap"
        >
          <iframe
            src={issue.webUrl}
            title={issue.title}
            data-testid="issue-html-frame"
            className="w-full block"
            style={{ height: "1800px", minHeight: "80vh" }}
          />
        </div>
      </div>
    </section>
  );
}
